// Shared date/time helpers — the frontend's single source of truth.
//
// Conventions:
//  - Instants (CreatedAt, activity timestamps, scheduled sends) arrive as ISO
//    strings with Z and are DISPLAYED in America/Los_Angeles via fmtDateTimeLA /
//    fmtDateLA. Never format an instant with browser-local or UTC.
//  - Date-only values (order dates, promo/pricing start/end, due dates) are
//    "YYYY-MM-DD" (sometimes "...T00:00:00.000Z" from the DB) and are displayed
//    by STRING SLICING via fmtDateOnly — never parsed with `new Date()` first,
//    which would shift them a day for Pacific users.
//  - "Today" for <input type="date"> defaults comes from todayYmd() (local
//    getters) — never `.toISOString().slice(0,10)`, which rolls to tomorrow
//    after 5 PM Pacific.

export const LA_TZ = "America/Los_Angeles";

const laPartsFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: LA_TZ,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function laPartsOf(d) {
  const p = {};
  for (const part of laPartsFmt.formatToParts(d)) p[part.type] = part.value;
  return p;
}

function parseDate(v) {
  if (v == null || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ── Instant display (LA) ─────────────────────────────────────────────── */

/** Format an instant (ISO string / Date / epoch ms) as LA date + time. */
export function fmtDateTimeLA(v, opts) {
  const d = parseDate(typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    timeZone: LA_TZ,
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  });
}

/** Format an instant as its LA calendar date only. */
export function fmtDateLA(v, opts) {
  const d = parseDate(typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { timeZone: LA_TZ, ...opts });
}

/* ── Date-only display ────────────────────────────────────────────────── */

/**
 * Display a date-only value ("YYYY-MM-DD", "YYYY-MM-DDT00:00:00.000Z", or a
 * Date read from a date-only DB column) as M/D/YYYY by string slicing —
 * never constructs a Date from a date-only string.
 */
export function fmtDateOnly(v) {
  if (!v) return "";
  let ymd = "";
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) ymd = m[0];
  } else if (v instanceof Date && !Number.isNaN(v.getTime())) {
    ymd = v.toISOString().slice(0, 10);
  }
  if (!ymd) {
    const d = parseDate(v);
    if (!d) return "";
    ymd = d.toISOString().slice(0, 10);
  }
  const [y, mo, da] = ymd.split("-");
  return `${Number(mo)}/${Number(da)}/${y}`;
}

/* ── "Today" and date math ────────────────────────────────────────────── */

/** Local "YYYY-MM-DD" for <input type="date"> defaults and filenames. */
export function todayYmd(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Add n calendar days to a "YYYY-MM-DD" string (pure UTC math). */
export function addDaysYmd(ymd, n) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + Number(n)));
  const pad = (x) => String(x).padStart(2, "0");
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/* ── Client-side range filtering from <input type="date"> values ──────── */

/** Local start of day for a "YYYY-MM-DD" input value (NOT new Date(ymd), which is UTC). */
export function startOfDayLocal(ymd) {
  return new Date(`${ymd}T00:00:00`);
}

/** Local end of day for a "YYYY-MM-DD" input value. */
export function endOfDayLocal(ymd) {
  return new Date(`${ymd}T23:59:59.999`);
}

/* ── <input type="datetime-local"> round trip ─────────────────────────── */

/** Instant → "YYYY-MM-DDTHH:MM" in browser-local time, for a datetime-local value. */
export function fmtLocalInput(isoLike) {
  const d = parseDate(isoLike);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value (browser-local) → ISO UTC string for the API. */
export function localInputToISO(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Local-now floor for min= on datetime-local inputs (never toISOString().slice). */
export function minDatetimeLocalNow(padMs = 60000) {
  return fmtLocalInput(new Date(Date.now() + padMs));
}

/* ── Pacific wall-clock ↔ UTC ─────────────────────────────────────────── */

/** "YYYY-MM-DD" of an instant as seen on an LA wall clock. */
export function ymdLA(d = new Date()) {
  const p = laPartsOf(d);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * The UTC instant at which an LA wall clock reads `ymd hh:mm`.
 * Guess-and-correct handles DST transitions.
 */
export function laWallToUtc(ymd, hhmm = "00:00") {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const [hh, mi] = String(hhmm).split(":").map(Number);
  const intended = Date.UTC(y, m - 1, d, hh || 0, mi || 0, 0);
  let guess = intended;
  for (let i = 0; i < 2; i++) {
    const p = laPartsOf(new Date(guess));
    const seen = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour), Number(p.minute), Number(p.second)
    );
    guess += intended - seen;
  }
  return new Date(guess);
}

/* ── Scheduled sends (Pacific-anchored) ───────────────────────────────── */

function isWeekendYmd(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * Compute the scheduled send instant for reply/email scheduling options.
 * "next_morning" = next business day 8:30 AM *Pacific* regardless of the
 * browser's timezone. "custom" accepts a datetime-local value (browser-local —
 * acceptable; users are PT). Returns ISO UTC string or null (= send now).
 */
export function computeScheduledAtPacific(option, customValue) {
  if (!option || option === "now") return null;
  const now = new Date();
  if (option === "30min") return new Date(now.getTime() + 30 * 60 * 1000).toISOString();
  if (option === "1hr") return new Date(now.getTime() + 60 * 60 * 1000).toISOString();
  if (option === "next_morning") {
    let ymd = addDaysYmd(ymdLA(now), 1);
    while (isWeekendYmd(ymd)) ymd = addDaysYmd(ymd, 1);
    return laWallToUtc(ymd, "08:30").toISOString();
  }
  if (option === "custom" && customValue) {
    const d = new Date(customValue);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}
